
import React, { useEffect, useState } from "react";
import { TableBody, TableRow, TableCell } from "@/components/ui/table";

interface ScoresState {
  [key: string]: (number | null)[];
}

interface LevelInfo {
  level: string;
}

interface StandardTableBodyProps {
  testPoints: string[];
  scores: ScoresState;
  isSpecialPutts: boolean;
  getPointTotal: (point: string) => number;
  getGrandTotal: () => number;
  getTestDistances: (testId: string) => Record<string, string>;
  selectedTest: string | null;
  levels: Record<string, string | LevelInfo>;
  isWedgingTest?: boolean;
  isBunkerTest?: boolean;
}

const StandardTableBody: React.FC<StandardTableBodyProps> = ({
  testPoints,
  scores,
  isSpecialPutts,
  getPointTotal,
  getGrandTotal,
  getTestDistances,
  selectedTest,
  levels,
  isWedgingTest = false,
  isBunkerTest = false
}) => {
  const [displayPoints, setDisplayPoints] = useState<string[]>([]);
  
  // Get distances from the getTestDistances function
  const distances = selectedTest ? getTestDistances(selectedTest) : {};
  
  useEffect(() => {
    if (testPoints && testPoints.length > 0) {
      setDisplayPoints([...testPoints]);
      console.log("StandardTableBody - Setting display points:", testPoints);
      console.log("StandardTableBody - Current scores:", scores);
    }
  }, [testPoints, scores]);
  
  // Debug logging pour vérifier les points et les scores
  useEffect(() => {
    console.log("StandardTableBody - testPoints:", testPoints);
    console.log("StandardTableBody - scores:", scores);
    console.log("StandardTableBody - selectedTest:", selectedTest);
    console.log("StandardTableBody - distances:", distances);
  }, [testPoints, scores, selectedTest, distances]);
  
  const formatDistance = (point: string) => {
    console.log("formatDistance - point:", point, "distances:", distances);
    // Use distances from the getTestDistances function if available
    if (distances && distances[point]) {
      // Ensure distances always have the "m" unit
      if (!isNaN(Number(distances[point]?.replace('m', ''))) && !distances[point]?.includes('m')) {
        return `${distances[point]}m`;
      }
      return distances[point];
    }
    
    // Level 1 specific fallback for different test types
    if (selectedTest?.includes('putts-')) {
      if (point === 'G' || point.startsWith('G')) return '6m';
      if (point === 'H' || point.startsWith('H')) return '10m';
      if (point === 'I' || point.startsWith('I')) return '12m';
    } else if (selectedTest?.includes('chipping-')) {
      if (selectedTest === 'chipping-roulee-plat') {
        if (point === 'G2') return '10m';
        if (point === 'H2') return '20m';
        if (point === 'I2') return '30m';
      } else {
        if (point === 'G' || point === 'G1') return '20m';
        if (point === 'H' || point === 'H1') return '30m';
        if (point === 'I' || point === 'I1') return '40m';
      }
    } else if (selectedTest?.includes('wedging-')) {
      if (selectedTest === 'wedging-mid') {
        if (point === 'G2') return '75m';
        if (point === 'H2') return '85m';
        if (point === 'I2') return '95m';
      } else if (selectedTest === 'wedging-pente-gauche') {
        if (point === 'G1') return '60m';
        if (point === 'H1') return '80m';
        if (point === 'I1') return '100m';
      } else {
        if (point === 'G') return '60m';
        if (point === 'H') return '80m';
        if (point === 'I') return '100m';
      }
    } else if (selectedTest?.includes('bunker-')) {
      if (selectedTest === 'bunker-long') {
        if (point === 'G1') return '15m';
        if (point === 'H1') return '20m';
      } else {
        if (point === 'G') return '5m';
        if (point === 'H') return '8m';
        if (point === 'I') return '12m';
      }
    } else if (selectedTest === 'fairwayWood-3') {
      if (point === 'G') return '200m';
    }
    
    return point;
  };

  // Fonction pour mapper les points affichés (G, H, I) aux points de score correspondants (WedgingA, etc.)
  const getPointScore = (point: string): (number | null)[] => {
    // Si le test est de type wedging
    if (selectedTest?.includes('wedging-')) {
      if (selectedTest === 'wedging-mid') {
        // Pour l'exercice wedging-mid spécifiquement
        if (point === 'G2') return scores['WedgingD'] || Array(10).fill(null);
        if (point === 'H2') return scores['WedgingE'] || Array(10).fill(null);
        if (point === 'I2') return scores['WedgingF'] || Array(10).fill(null);
      } else if (selectedTest === 'wedging-cercles') {
        if (point === 'G') return scores['WedgingA'] || Array(10).fill(null);
        if (point === 'H') return scores['WedgingB'] || Array(10).fill(null);
        if (point === 'I') return scores['WedgingC'] || Array(10).fill(null);
      } else if (selectedTest === 'wedging-pente-gauche') {
        if (point === 'G1') return scores['WedgingA1'] || Array(10).fill(null);
        if (point === 'H1') return scores['WedgingB1'] || Array(10).fill(null);
        if (point === 'I1') return scores['WedgingC1'] || Array(10).fill(null);
      }
    }
    // Si le test est de type bunker-long
    else if (selectedTest === 'bunker-long') {
      if (point === 'G1') return scores['G'] || Array(10).fill(null);
      if (point === 'H1') return scores['H'] || Array(10).fill(null);
    }
    
    // Par défaut, utiliser les scores directs
    return scores[point] || Array(10).fill(null);
  };

  return (
    <TableBody>
      {displayPoints.map((point) => {
        // Récupérer les scores avec la fonction de mappage
        const pointScores = getPointScore(point);
        console.log(`StandardTableBody - Scores pour le point ${point}:`, pointScores);
        
        return (
          <TableRow key={point}>
            <TableCell className="font-medium">
              {point}
            </TableCell>
            <TableCell className="font-medium text-green-600">
              {formatDistance(point)}
            </TableCell>
            {Array.from({ length: 10 }, (_, i) => (
              <TableCell key={i} className="text-center">
                {pointScores[i] !== null && pointScores[i] !== undefined
                  ? pointScores[i]
                  : '-'}
              </TableCell>
            ))}
            <TableCell className="text-center font-bold">
              {isWedgingTest ? 
                (selectedTest === 'wedging-mid' ?
                  (point === 'G2' ? getPointTotal('WedgingD') :
                   point === 'H2' ? getPointTotal('WedgingE') :
                   point === 'I2' ? getPointTotal('WedgingF') : 0) :
                 selectedTest === 'wedging-cercles' ?
                  (point === 'G' ? getPointTotal('WedgingA') :
                   point === 'H' ? getPointTotal('WedgingB') :
                   point === 'I' ? getPointTotal('WedgingC') : 0) :
                 selectedTest === 'wedging-pente-gauche' ?
                  (point === 'G1' ? getPointTotal('WedgingA1') :
                   point === 'H1' ? getPointTotal('WedgingB1') :
                   point === 'I1' ? getPointTotal('WedgingC1') : 0) : 0) :
                selectedTest === 'bunker-long' ?
                  (point === 'G1' ? getPointTotal('G') :
                   point === 'H1' ? getPointTotal('H') : 0) :
                getPointTotal(point)}
            </TableCell>
          </TableRow>
        );
      })}
      <TableRow>
        <TableCell colSpan={2} className="text-right font-bold">
          Total Points
        </TableCell>
        <TableCell colSpan={10} />
        <TableCell className="text-center font-bold">
          {getGrandTotal()}
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

export default StandardTableBody;
